import CarouselAbstr, { CarouselOptions } from './CarouselAbstr';
import imageLoader from '../../lib/imageLoader';
import { getQueryParamsByName } from '../../lib/urlUtilities';
import Lightbox from '../Lightbox/Lightbox';

interface ImgCarouselOptions extends CarouselOptions {
	carouselClass: string;
	doLazyLoad: boolean;
	doBackgroundedImage?: boolean;
	doHeightChange?: boolean;
	doInlineVideo?: boolean;
	doLightbox?: boolean;
	duration?: number;
	imageArr?: HTMLImageElement[];
}

export default class Carousel extends CarouselAbstr {
	private imageArr: HTMLImageElement[];
	private doLazyLoad: boolean;
	private doBackgroundedImage: boolean = true;
	private doHeightChange: boolean = false;
	private doInlineVideo: boolean = false;
	private doLightbox: boolean = false;

	private observer: IntersectionObserver;
	private intersectionOptions: IntersectionObserverInit = {
		root: null,
		rootMargin: '0px',
		threshold: 0.5
	};

	private lightbox: Lightbox;

	constructor(options: ImgCarouselOptions) {
		super(options);

		const slideEl = this.carouselEl.getElementsByClassName(`${this.carouselClass}__slide`);
		for (let i = 0; i < slideEl.length; i++) {
			this.slides.push(slideEl[i]);
		}
		this.imageArr = options.imageArr;
		if (!this.imageArr && !this.slides.length)
			return;

		this.doLazyLoad = options.doLazyLoad;
		if (options.doBackgroundedImage !== undefined) {
			this.doBackgroundedImage = options.doBackgroundedImage;
		}
		if (options.doHeightChange) {
			this.doHeightChange = options.doHeightChange;
		}
		if (options.doInlineVideo) {
			this.doInlineVideo = options.doInlineVideo;
		}
		if (options.doLightbox) {
			this.doLightbox = options.doLazyLoad;
		}

		this.init();
	}

	protected init(): void {
		if (this.slides.length) {
			this.loadSlides();
		} else {
			this.buildSlides(0, 1)
				.then((response) => {
					this.carouselEl.classList.add(this.carouselClass + '--loaded');
					this.currentIndex = 0;

					if (!this.doLazyLoad) {
						this.buildRemainingSlidesAndSetup();
					} else {
						this.observer = new IntersectionObserver(this.handleInViewBuild, this.intersectionOptions);
						this.observer.observe(this.carouselEl);
					}

					if (this.doHeightChange) {
						this.adjustHeight();
					}
				})
				.catch((error) => {
					console.warn(`Error: ${error}`);
				});
		}
	}

	private buildImageSlideEl(imageInfo: HTMLImageElement): Element {
		const slide: Element = document.createElement('div');
		slide.classList.add(this.carouselClass + '__slide');
		if (!this.slides.length) {
			slide.classList.add('active');
			this.currentIndex = 0;
		}

		const imageGravity = getQueryParamsByName('gravity', imageInfo.src);
		const imageTemplate = `<img src="${imageInfo.src}" alt="${imageInfo.alt}">`;

		const captionTemplate =
			imageInfo.title.length
				? `<div class="${this.carouselClass}__caption">${imageInfo.title}</div>`
				: '';

		if (this.doBackgroundedImage) {
			const backgroundedImageTemplate =
				`<span class="${this.carouselClass}__image image image--${imageGravity}" style="background-image: url(${imageInfo.src});">
					${imageTemplate}
				</span>`;

			slide.innerHTML = backgroundedImageTemplate + captionTemplate;
		} else {
			slide.innerHTML = imageTemplate + captionTemplate;
		}

		return slide;
	}

	private buildSlides(start: number = 0, end: number = this.imageArr.length): Promise<Response> {
		return new Promise((resolve, reject) => {
			// Images
			const srcs: string[] = [];
			const endNum: number = end <= this.imageArr.length ? end : this.imageArr.length;
			for (let i = start; i < endNum; i++) {
				const src: string = this.imageArr[i].src;
				srcs.push(src);
			}
			imageLoader(srcs).then(response => {
				for (let i = start; i < response.length + start; i++) {
					const slide = this.buildImageSlideEl(this.imageArr[i]);
					this.slides.push(this.carouselFrameEl.appendChild(slide));
				}
				resolve();
			}).catch((error) => {
				reject(error);
			});
		});
	}

	protected loadSlides(): void {
		this.currentIndex = 0;
		this.carouselEl.classList.add(this.carouselClass + '--loaded');

		if (!this.doLazyLoad) {
			this.loadRemainingSlidesAndSetup();
		} else {
			this.observer = new IntersectionObserver(this.handleInViewLoad, this.intersectionOptions);
			this.observer.observe(this.carouselEl);
		}
	}

	private handleInViewBuild: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]): void => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				this.buildRemainingSlidesAndSetup();
				this.observer.unobserve(this.carouselEl);
			}
		});
	}

	private handleInViewLoad: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]): void => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				this.loadRemainingSlidesAndSetup();
				this.observer.unobserve(this.carouselEl);
			}
		});
	}

	private loadRemainingSlidesAndSetup(): void {
		let srcs: string[] = [];
		for (let i = 1; i < this.slides.length; i++) {
			const slideEl = this.slides[i];
			const slideImgSrc = slideEl.getAttribute('data-img-src');
			if (!slideImgSrc)
				continue;

			srcs.push(slideImgSrc);
		}

		imageLoader(srcs)
			.then(response => {
				for (let i = 1; i < this.slides.length; i++) {
					const slideEl = this.slides[i];
					const slideImage = slideEl.getElementsByClassName('image')[0] as HTMLElement;
					const slideImgSrc = response[i - 1];
					slideImage.style.backgroundImage = `url(${slideImgSrc})`;

					const slideImageEl = slideEl.getElementsByTagName('img')[0];
					slideImageEl.src = slideImgSrc;
				}

				this.finalSetup();
			})
			.catch(err => console.log(err));
	}
	private buildRemainingSlidesAndSetup: Function = () => {
		this.buildSlides(this.slides.length)
			.then(() => this.finalSetup())
			.catch((err) => {
				console.warn(`Error: ${err}`);
			});
	}

	private finalSetup(): void {
		if (this.onInit) {
			this.onInit();
		}
		this.initAutoAdvance();
		this.buildControls();
		this.buildIndicators();
		this.buildPagination();
		this.buildLightbox();
	}

	protected onChange = (): void => {
		if (this.doHeightChange) {
			this.adjustHeight();
		}
	}

	protected adjustHeight(): void {
		const newSlideImage = this.slides[this.currentIndex].getElementsByTagName('img')[0];

		const frameEl = this.carouselEl.getElementsByClassName(`${this.carouselClass}__frame`)[0] as HTMLElement;

		this.carouselFrameEl.style.height = `${newSlideImage.naturalHeight}px`;
		this.carouselFrameEl.style.width = `${newSlideImage.naturalWidth}px`;
		this.carouselFrameEl.style.height = `${newSlideImage.getBoundingClientRect().height}px`;
		this.carouselFrameEl.style.width = `${newSlideImage.getBoundingClientRect().width}px`;
	}

	private buildLightbox(): void {
		if (this.doLightbox) {
			this.carouselEl.classList.add(`${this.carouselClass}--hasLightbox`);

			const imgEl = this.carouselEl.getElementsByTagName('img');
			let imgArr: HTMLImageElement[] = [];
			for (let i = this.currentIndex; i < imgEl.length; i++) {
				imgArr.push(imgEl[i]);
			}

			this.lightbox = new Lightbox({
				type: 'carousel',
				triggerEl: [this.carouselFrameEl],
				onOpen: () => {
					clearTimeout(this.timeout);
					const lightboxCarousel = new Carousel({
						carouselEl: this.lightbox.el,
						carouselClass: 'lightbox',
						doLazyLoad: false,
						doBackgroundedImage: false,
						doHeightChange: true,
						doPagination: true,
						imageArr: imgArr,
						onInit: () => {
							lightboxCarousel.activateByIndex(this.currentIndex);
						}
					});
				},
				onClose: () => {
					this.initAutoAdvance();
				}
			});
		}
	}
}
